// supervisor-bootstrap.ts
//
// First-`run` step (architecture §6.1: "first run: commit-owner.bootstrapCommit
// (init files) <- intent -> policy -> commit -> receipt"). `autopilot init`
// (P5) deliberately leaves the worktree uncommitted; this is the ONLY place
// that turns it into the mission's first commit — same intent/policy/commit/
// receipt path as every other commit (commit-owner.ts, spec: "Không có
// đường commit nào khác ngoài commit-owner.ts").
//
// Idempotent: `bootstrapCommit` (commit-owner.ts) stages `manifestPaths`
// then only commits if something is actually staged — a repeat call with
// nothing new to commit is a safe no-op ("no_change"), so this module does
// NOT gate on "does HEAD already exist" (P9b fix, code-reviewer-260909-p8-
// m4.md follow-up class): a BROWNFIELD source repo (the common case — most
// real repos already have commits) already has a HEAD the moment
// `initMissionWorktree` checks it out, so a `hasHead` early-return made
// this a permanent no-op for brownfield missions, leaving `.claude/*`
// (pack) and `.avatar/tickets/*` (bootstrap-seeded survey+plan tickets)
// PERMANENTLY UNTRACKED in the worktree — the very first control-loop tick's
// `recoverOwnedCheckout` (`git clean -fd`) then deleted them outright
// (proven via a real CLI E2E: init on a repo with an existing commit, then
// `run` — `.claude/` vanished after tick 1, every ticket then failed
// preflight). `bootstrapCommit`'s own "no_change" branch already gives
// this function its idempotency for the "nothing new to commit" case
// (crash-and-restart, or a second `run` after this one already committed),
// so it needs no separate signal.
import { bootstrapCommit, type GitRunner } from "./commit-owner.js";
import type { MissionStore } from "./mission-store.js";
import type { PolicyAdapterInput, PolicyVerdict } from "./policy-adapter.js";

export interface EnsureBootstrapCommitInput {
  store: MissionStore;
  repoDir: string;
  gitRunner: GitRunner;
  evaluatePolicy: (input: PolicyAdapterInput) => Promise<PolicyVerdict>;
  /** Exactly the paths `autopilot init` left uncommitted (`.avatar/*`, ignore entries, seeded tickets). */
  manifestPaths: string[];
  missionId: string;
  runId: string;
}

export interface BootstrapCommitOutcome {
  performed: boolean;
  headSha?: string;
  reason?: string;
}

/**
 * Bootstraps the mission's first commit exactly once. Safe to call on every
 * `run` — `bootstrapCommit` itself is the idempotency boundary (stages
 * `manifestPaths`, commits only if something is actually staged), so a
 * repeat call once init files are already committed is a no-op ("no_change")
 * regardless of whether the underlying repo had a HEAD before this mission
 * even started (brownfield) or not (greenfield).
 */
export async function ensureBootstrapCommit(
  input: EnsureBootstrapCommitInput,
): Promise<BootstrapCommitOutcome> {
  const result = await bootstrapCommit({
    store: input.store,
    repoDir: input.repoDir,
    gitRunner: input.gitRunner,
    evaluatePolicy: input.evaluatePolicy,
    manifestPaths: input.manifestPaths,
    missionId: input.missionId,
    runId: input.runId,
  });

  if (result.status === "committed") {
    return { performed: true, headSha: result.headSha };
  }
  if (result.status === "policy_denied") {
    throw new Error(`supervisor-bootstrap: policy denied bootstrap commit: ${result.reason}`);
  }
  // "no_change": nothing was uncommitted (e.g. an empty init) — HEAD is
  // still unborn, but there is nothing this module can do about that; the
  // caller's next hasHead() check on a later run will keep retrying.
  return { performed: false, reason: "no_change" };
}
